import React from 'react';
import $ from 'jquery';
import { Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectedSidepanelName } from '../../Redux/SelectedSidepanelSlice';
import { ReactComponent as GoBackOutlined } from '../../assets/sidepanel/back.svg';
import useTranslation from '../../hooks/useTranslation';

import PdfViewer from '../PdfViewer';
import SidebarMenu from './Sidebar/Menu';

import SidepanelAnnotations from './Sidebar/Elements/Annotations';
import SidepanelAnnotationProperties from './Sidebar/Elements/AnnotationProperties';
import SidepanelAttachments from './Sidebar/Elements/Attachments';
import SidepanelBookmarks from './Sidebar/Elements/Bookmarks';
import SidepanelCompare from './Sidebar/Elements/Compare';
import SidepanelLayers from './Sidebar/Elements/Layers';
import SidepanelSearch from './Sidebar/Elements/Search';
import SidepanelSignatures from './Sidebar/Elements/Signatures';
import SidepanelThumbnails from './Sidebar/Elements/Thumbnails';

import styles from './MainContent.module.scss';
import { ReduxUpdater } from '../../pdf/ReduxUpdater';
import { GlobalDialogs } from '../../pdf/GlobalDialogs';
import type { DocumentApiHistory, UserApiConfiguration } from '../../pdf/Interfaces';
import { devError, devLog, devWarning } from '../../utils';
import { PdfViewerViewModeFlag, isValidViewMode } from '../../pdf/Enums';
import { backendApiUrl } from '../../App';
import api from '../../api';
import { GlobalConstants } from '../../pdf/GlobalConstants';
import { eplassSliceSelections } from '../../Redux/EplassSlice';
import { ApryseSDK } from '../../pdf/ApryseSDK';
import { selectedStateName } from '../../Redux/SelectedStateSlice';
import { GlobalVariables } from '../../pdf/GlobalVariables';
import { initSettingsError } from '../../Redux/InitSettingsSlice';
import { userSettingsError } from '../../Redux/UserSettingsSlice';

interface IMainContentProps {
    customFontURL?: string;
    language: string;
    license: string;
}

export default (props: IMainContentProps) => {
    const i18next_t = useTranslation();

    const selectedSidepanel = useSelector(selectedSidepanelName);
    const selections = useSelector(eplassSliceSelections);
    const stateName = useSelector(selectedStateName);

    const initError = useSelector(initSettingsError);
    const userError = useSelector(userSettingsError);

    // const refreshFitZoom = React.useCallback(() => {
    //     // ToDO: // If not an overlay and zoom mode is one of the string values, adjust zoom
    //     // if (context && GlobalVariables.docRender) {
    //     //     const zoom = context.docRender.getScale();

    //     //     if (typeof zoom === 'string') {
    //     //         GlobalVariables.docRender.pdfViewer.zoomTo(zoom).catch(GlobalDialogs.showError);
    //     //     }
    //     // }
    // }, [context]);

    const onResize = (): void => {
        // ToDO: debounce(refreshFitZoom, 250);
    };

    // Fit zoom when resizing the window.
    React.useEffect(
        () => {
            GlobalVariables.loadUserCategories();

            $(window).on('resize', onResize);

            return () => {
                $(window).off('resize', onResize);
            };
        },
        [/* ToDO: refreshFitZoom */]
    );

    const onPdfOpened = () => {
        devLog('onPdfOpened...');
        /**
         * Since appending an element later on causes the element
         * to be at the end of the document... a workaround for this
         * is adding it right at the beginning (as a hidden placeholder)...
         */
        $('.viewer-container').append('<div id="measurement-info" style="display: none;"></div>');
        ApryseSDK.setLanguage(props.language);

        if (process.env.REACT_APP_IGNORE_USER_CONFIG?.toLocaleLowerCase() === 'true') {
            ReduxUpdater.viewMode =
                ReduxUpdater.documentUrl2 !== ''
                    ? PdfViewerViewModeFlag.Compare
                    : PdfViewerViewModeFlag.View;
            devWarning(
                'Fetched user config will not be applied: ignore-flag is set in dev-env variables',
            );
            return;
        }

        const documentHistoryURL = backendApiUrl.document.history;
        const documentInfoURL = backendApiUrl.document.info;
        const viewerKey = [
            ...GlobalConstants.supported.documents,
            ...GlobalConstants.supported.images,
        ]
            .map(extension => ReduxUpdater.documentUrl?.endsWith(extension))
            .some(v => v === true)
            ? ''
            : ReduxUpdater.documentUrl;

        api.get<UserApiConfiguration>(
            backendApiUrl.user.settings,
            viewerKey ? { headers: { viewerKey } } : undefined,
        )
            .then(
                settingsData => {
                    if (ReduxUpdater.documentUrl2 !== '') {
                        ReduxUpdater.viewMode = PdfViewerViewModeFlag.Compare;
                    } else {
                        ReduxUpdater.viewMode = isValidViewMode(settingsData.viewMode)
                            ? settingsData.viewMode
                            : PdfViewerViewModeFlag.View;
                    }

                    if (settingsData?.presets) {
                        if (settingsData.presets.lists) {
                            ReduxUpdater.lists = settingsData.presets.lists;
                        }

                        if (settingsData.presets.selected?.color) {
                            ReduxUpdater.selectedColor = settingsData.presets.selected.color;
                            ReduxUpdater.useWorkflowColor =
                                settingsData.presets.lists?.colors?.length === 1;
                        }
                    }
                },
                error => onUserConfigError(error),
            )
            .catch(error => onUserConfigError(error));

        if (ReduxUpdater.documentUrl) {
            api.get<DocumentApiHistory>(documentInfoURL, {
                headers: {
                    viewerKey: ReduxUpdater.documentUrl,
                },
            })
                .then(
                    documentData => {
                        devLog(documentData);
                        ReduxUpdater.documentName = documentData.docNumber;
                        ReduxUpdater.documentNoteID = documentData.NoteId;
                        ReduxUpdater.documentVersion = documentData.docIndex;
                        ReduxUpdater.documentSubject = documentData.subject;
                        ReduxUpdater.annotationSidepanelRefresh();
                    },
                    error => onUserConfigError(error),
                )
                .catch(error => onUserConfigError(error));

            api.get<DocumentApiHistory[]>(documentHistoryURL, {
                headers: {
                    viewerKey: ReduxUpdater.documentUrl,
                },
            })
                .then(
                    historyData => {
                        ReduxUpdater.documentHistory = historyData;
                        ReduxUpdater.annotationSidepanelRefresh();
                    },
                    error => onUserConfigError(error),
                )
                .catch(error => onUserConfigError(error));
        }

        if (ReduxUpdater.documentUrl2) {
            api.get<DocumentApiHistory>(documentInfoURL, {
                headers: {
                    viewerKey: ReduxUpdater.documentUrl2,
                },
            })
                .then(
                    documentData => {
                        devLog(documentData);
                        ReduxUpdater.documentName2 = documentData.docNumber;
                        ReduxUpdater.documentNoteID2 = documentData.NoteId;
                        ReduxUpdater.documentVersion2 = documentData.docIndex;
                        ReduxUpdater.documentSubject2 = documentData.subject;
                        ReduxUpdater.annotationSidepanelRefresh();
                    },
                    error => onUserConfigError(error),
                )
                .catch(error => onUserConfigError(error));

            api.get<DocumentApiHistory[]>(documentHistoryURL, {
                headers: {
                    viewerKey: ReduxUpdater.documentUrl2,
                },
            })
                .then(
                    historyData => {
                        ReduxUpdater.documentHistory2 = historyData;
                        ReduxUpdater.annotationSidepanelRefresh();
                    },
                    error => onUserConfigError(error),
                )
                .catch(error => onUserConfigError(error));
        }

        // Show any stored messages and empty queue
        GlobalDialogs.showNotyfications();
    };

    const onUserConfigError = (error: Error) => {
        devLog('Error fetching user config or ratio data.');
        devError(error);
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.View;
    };

    const onPdfError = (error: Error) => {
        devLog('Error opening pdf file');
        devError(error);
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.Error;
    };

    const sidepanelContent = React.useMemo(() => {
        switch (selectedSidepanel) {
            case 'annotations':
                return <SidepanelAnnotations />;
            case 'attachments':
                return <SidepanelAttachments />;
            case 'bookmarks':
                return <SidepanelBookmarks />;
            case 'compare':
                return <SidepanelCompare />;
            case 'layers':
                return <SidepanelLayers />;
            case 'properties':
                return <SidepanelAnnotationProperties />;
            case 'search':
                return <SidepanelSearch />;
            case 'signatures':
                return <SidepanelSignatures />;
            case 'thumbnails':
                return <SidepanelThumbnails />;
            default:
                return <></>;
        }
    }, [selectedSidepanel]);

    React.useMemo(() => {
        ApryseSDK.updateSelections(selections);
    }, [selections, stateName]);

    React.useMemo(() => {
        if (initError) {
            ReduxUpdater.viewMode = PdfViewerViewModeFlag.Error;
        }
    }, [initError]);

    return (
        <>
            <Grid item className={styles.sidebarMenu}>
                <Paper className={styles.fullHeight}>
                    <Stack className={styles.sidebarMenuButtons}>
                        <SidebarMenu />
                    </Stack>
                </Paper>
            </Grid>
            {selectedSidepanel !== null ? (
                <Grid item className={styles.sidebar}>
                    <Paper className={styles.fullHeight}>
                        <Stack direction="row">
                            <Typography className={styles.sidebarTitle}>
                                {i18next_t(`sidebar.title.${selectedSidepanel}`)}
                            </Typography>
                            <IconButton
                                className={styles.sidebarTitleGoBack}
                                onClick={() => (ReduxUpdater.selectedSidepanel = null)}
                            >
                                <GoBackOutlined />
                            </IconButton>
                        </Stack>
                        <Stack style={{ width: 'auto', flexGrow: 1 }}>{sidepanelContent}</Stack>
                    </Paper>
                </Grid>
            ) : (
                <></>
            )}
            <Grid
                item
                style={{
                    width: `calc(100vw - 68px - ${
                        selectedSidepanel === null ? 0 : GlobalConstants.sidebarWidth
                    }px`,
                }}
            >
                <Paper>
                    <PdfViewer
                        className={styles.pdfViewer}
                        onPdfOpened={onPdfOpened}
                        onPdfError={onPdfError}
                        lic={props.license}
                        customFontURL={props.customFontURL}
                    />
                </Paper>
            </Grid>
        </>
    );
};
